<template>
  <div class="v-mentionable" :class="{ 'has-label': label }">
    <VPopper
      ref="popper"
      lazy
      mount-to="#v-poppers"
      :offset="5"
      placement="bottom-start"
      transition="v-zoom-in-top"
      trigger="manual"
      @destroy-popper="reset"
      @show="popperVisible = true"
    >
      <template #reference>
        <VTextarea ref="vtextarea" v-model="text" :disabled :error :label :placeholder :prop :rows :suggestions>
          <template v-if="$slots.prefix" #prefix>
            <slot name="prefix" />
          </template>
        </VTextarea>

        <div ref="overlay" class="v-mentionable-overlay" :style="{ height: `${textareaHeight}px`, paddingLeft, paddingRight }">
          <template v-for="(item, index) in modelValue" :key="index">
            <a v-if="'key' in item && item.key in mentions" class="v-mention">
              <slot :mention="mentions[item.key]" name="mention">{{ item.text }}</slot>
            </a>
            <template v-else>{{ item.text }}</template>
          </template>
          <div v-if="text.endsWith('\n')">&nbsp;</div>
        </div>
      </template>

      <ul v-if="popperVisible" class="v-select-options" :style="{ width: `${optionsWidth}px` }">
        <li v-if="fetching" style="padding: 10px 0">
          <VLoadingDots :size="6" />
        </li>

        <li v-else-if="searchQuery && visibleOptions.length === 0" class="v-option-group__title">
          <slot name="options-empty">{{ i18n.noResultsFoundForYourInput }}</slot>
        </li>

        <li v-else-if="visibleOptions.length === 0" class="v-option-group__title">
          <slot name="options-empty">{{ i18n.noResultsFound }}</slot>
        </li>

        <div class="v-select-options__items">
          <template v-for="(option, index) in visibleOptions" :key="index">
            <li :class="{ 'v-select-option': true, active: index === activeIndex }" @click="selectOption(option)" @mousedown.prevent>
              <slot name="option" :option>
                <div class="v-select-option__left">
                  {{ option.label }}
                </div>
                <div v-if="option.labelRight" class="v-select-option__right">
                  {{ option.labelRight }}
                </div>
              </slot>
            </li>
          </template>
        </div>
      </ul>
    </VPopper>
  </div>
</template>

<script setup lang="ts" generic="TMention extends Mention = Mention">
import { debounce } from 'lodash-es'
import { computed, onMounted, onUnmounted, type Ref, ref, useTemplateRef, watch } from 'vue'

import { useResizeObserver } from '../composables/useResizeObserver.ts'
import { i18nSelect as i18n } from '../utils/i18n.ts'
import VLoadingDots from './VLoadingDots.vue'
import VPopper from './VPopper.vue'
import VTextarea from './VTextarea.vue'

export interface Mention {
  label: string
  key: any
  labelRight?: string
  searchText?: string
}

export type ModelValue = (
  | {
      text: string
    }
  | {
      text: string
      key: any
      label: string
    }
)[]

interface Props {
  disabled?: boolean
  options: TMention[] | ((query: string) => Promise<TMention[]>)
  error?: string | string[]
  triggerKey?: string
  label?: string
  placeholder?: string
  prop?: string
  rows?: number
  suggestions?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  error: '',
  triggerKey: '@',
  label: '',
  placeholder: '',
  prop: '',
  rows: 3,
  suggestions: () => [],
})

const model = defineModel<ModelValue>({ required: true })

defineSlots<{
  default?(): any
  prefix?(props: any): any
  option?(props: { option: TMention }): any
  'options-empty'?(props: any): any
  mention?(props: { mention: TMention }): any
}>()

let textarea: HTMLTextAreaElement
const textareaHeight = ref(0)
const overlay = useTemplateRef('overlay')
const popper = useTemplateRef('popper')
const popperVisible = ref(false)
const vtextarea = useTemplateRef('vtextarea')
const optionsWidth = ref(0)
const activeIndex = ref(0)
const _options = ref<TMention[]>([]) as Ref<TMention[]>
const fetching = ref(false)
const startOfQuery = ref(-1)
const endOfQuery = ref(-1)
const paddingLeft = ref('')
const paddingRight = ref('')

const text = ref('')
watch(
  model,
  (value) => {
    text.value = value.map((part) => part.text).join('')
  },
  { immediate: true },
)

watch(
  () => text.value,
  () => updateModelValue(),
)

type Mentions = Record<string | number, TMention>
const mentions = computed((): Mentions => {
  const mentions: Mentions = {}

  for (const option of _options.value) {
    if (option.key in mentions) {
      continue
    }

    const regex = new RegExp(`(\\s|^)${props.triggerKey}${option.label}(?=\\s|\\b|$)`, 'gm')
    if (regex.test(text.value)) {
      mentions[option.key] = option
    }
  }

  return mentions
})
/**
 * What the user has typed after the `triggerKey`.
 */
const searchQuery = computed(() => {
  if (startOfQuery.value === -1 || endOfQuery.value === -1 || startOfQuery.value === endOfQuery.value) {
    return ''
  }

  return text.value.substring(startOfQuery.value + 1, endOfQuery.value + 1).toLowerCase()
})

/**
 * Filtered options based on what the user has typed.
 */
const visibleOptions = computed((): TMention[] => {
  const query = searchQuery.value

  if (!query || !Array.isArray(props.options)) {
    return _options.value
  }

  return _options.value.filter((_options) => {
    if (_options.searchText?.toLowerCase().includes(query)) {
      return true
    }

    if (_options.labelRight?.toLowerCase().includes(query)) {
      return true
    }

    return _options.label.toLowerCase().includes(query)
  })
})

function onReferenceResize({ width }: DOMRectReadOnly) {
  optionsWidth.value = width
}

/**
 *
 * Sync the overlay dimensions with the underlying textarea.
 */
function onTextareaResize({ height }: DOMRectReadOnly) {
  textareaHeight.value = height
  if (vtextarea.value?.textarea) {
    paddingLeft.value = vtextarea.value.paddingLeft
    paddingRight.value = vtextarea.value.paddingRight
  }
}

const fetchOptions = debounce(async () => {
  if (Array.isArray(props.options)) {
    return
  }

  fetching.value = true
  try {
    const data = await props.options(searchQuery.value)
    _options.value = data
  } finally {
    fetching.value = false
  }
}, 300)

watch(
  () => props.options,
  (value) => {
    if (Array.isArray(value)) {
      _options.value = value
    } else {
      fetchOptions()
    }
  },
  { immediate: true },
)

/**
 * Get the char index of the caret inside the textarea.
 */
function getCaretIndex() {
  return (textarea.isContentEditable ? window.getSelection()?.anchorOffset : textarea.selectionStart) ?? 0
}

/**
 * Handle keydown events when the popper menu is visible.
 */
function onKeyDown(event: KeyboardEvent) {
  const key = event.key

  if (!popperVisible.value) {
    if (key === props.triggerKey) {
      endOfQuery.value = getCaretIndex()
      // Show the popper if the triggerKey was pressed with a whitespace character before it.
      if (/\s/.test(text.value[endOfQuery.value - 1])) {
        startOfQuery.value = endOfQuery.value
        popper.value?.show()
        fetchOptions()
      }
    }

    return
  }

  if (key === 'ArrowDown') {
    if (activeIndex.value < visibleOptions.value.length - 1) {
      activeIndex.value++
    }
    event.preventDefault()

    return
  }

  if (key === 'ArrowUp') {
    if (activeIndex.value > 0) {
      activeIndex.value--
    }
    event.preventDefault()

    return
  }

  if (key === 'Enter') {
    // Select active option or hide the menu
    const activeOption = visibleOptions.value[activeIndex.value]
    if (activeOption) {
      event.preventDefault()
      selectOption(activeOption)
    } else {
      hide()
    }

    return
  }

  const lastCaretIndex = getCaretIndex()

  if (key === 'Escape' || key === 'Home' || key === 'PageDown' || key === 'PageUp') {
    hide()

    return
  }

  if (key === 'ArrowLeft') {
    // Hide when moving too far to left
    if (lastCaretIndex === startOfQuery.value + 1) {
      hide()
    }

    return
  }

  if (key === 'ArrowRight') {
    // Hide when moving too far to right
    if (lastCaretIndex > endOfQuery.value && model.value.length > lastCaretIndex) {
      hide()
    }

    return
  }

  // Hide when triggerKey is removed.
  if (key === 'Backspace') {
    if (lastCaretIndex === startOfQuery.value + 1) {
      hide()

      return
    }

    if (lastCaretIndex > startOfQuery.value && lastCaretIndex <= endOfQuery.value + 1) {
      endOfQuery.value--

      return
    }
  }

  if (popperVisible.value) {
    endOfQuery.value = lastCaretIndex
    fetchOptions()
  }
}

/**
 * Select the given option and replace the searchQuery text in the textarea with it.
 */
function selectOption(option: Mention) {
  const label = option.label
  const endOfMention = endOfQuery.value + 1
  const startOfMention = startOfQuery.value + 1

  const before = textarea.value.substring(0, startOfMention)
  const after = textarea.value.substring(endOfMention)
  textarea.value = `${before}${label} ${after}`
  textarea.selectionEnd = before.length + label.length + 1
  textarea.dispatchEvent(new Event('input'))

  hide()
}

/**
 * Hide the popper and reset internal state.
 */
function hide() {
  if (!popperVisible.value) {
    reset()
  } else {
    popper.value?.hide()
  }
}

function reset() {
  popperVisible.value = false
  activeIndex.value = 0
  endOfQuery.value = -1
  startOfQuery.value = -1
}

/**
 * Sync the scrollTop of the overlay element with the scrolltop of the textarea element.
 */
function syncScrollTop() {
  if (overlay.value) {
    overlay.value.scrollTop = textarea.scrollTop
  }
}

function onBlur() {
  popper.value?.hide()
}

/**
 * A hacky way to handle when a user for example replaces the entire textarea with Ctrl A and then does a Backspace.
 * We only know after Backspace press is up again (so not onKeydown) what the new content is.
 * Also check if the mentions prop is still accurate.
 */
function onInput() {
  if (text.value === props.triggerKey && !popperVisible.value) {
    startOfQuery.value = 0
    endOfQuery.value = 0
    popper.value?.show()
    fetchOptions()
  } else if (text.value === '') {
    reset()
  }
}

function updateModelValue() {
  if (Object.keys(mentions.value).length === 0) {
    model.value = text.value ? [{ text: text.value }] : []

    return
  }

  const mentionLabels = Object.values(mentions.value).map((mention) => `${props.triggerKey}${mention.label}`)
  const regex = new RegExp(`(${mentionLabels.map((label) => label.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g')
  const parts = text.value.split(regex)

  const value: ModelValue = parts.map((part) => {
    const index = mentionLabels.indexOf(part)
    if (index >= 0) {
      const mention = Object.values(mentions.value)[index]

      return { text: part, key: mention.key, label: mention.label }
    }

    return { text: part }
  })

  model.value = value
}

onMounted(() => {
  if (popper.value?.reference) {
    useResizeObserver(popper.value?.reference, onReferenceResize)
  }
  if (vtextarea.value?.textarea) {
    useResizeObserver(vtextarea.value?.textarea, onTextareaResize)
  }

  textarea = vtextarea.value?.textarea as HTMLTextAreaElement

  textarea.addEventListener('input', onInput)
  textarea.addEventListener('keydown', onKeyDown)
  textarea.addEventListener('scroll', syncScrollTop)
  textarea.addEventListener('keyup', syncScrollTop)
  textarea.addEventListener('blur', onBlur)
})

onUnmounted(() => {
  textarea.removeEventListener('input', onInput)
  textarea.removeEventListener('keydown', onKeyDown)
  textarea.removeEventListener('scroll', syncScrollTop)
  textarea.removeEventListener('keyup', syncScrollTop)
  textarea.removeEventListener('blur', onBlur)
})

defineExpose({
  vtextarea,
})
</script>

<style>
.v-mentionable {
  position: relative;

  .v-textarea textarea {
    color: transparent;
    caret-color: black;

    &::selection {
      color: transparent;
      background-color: #0074ffcc;
    }
  }

  &.has-label .v-mentionable-overlay {
    top: 22px;
    font-size: 20px;
  }
}

.v-mentionable-overlay {
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: left;

  &:not(.v-mentionable-overlay--readonly) {
    position: absolute;
    top: 6px;
    padding: 0 20px 0 0;
    pointer-events: none;
    overflow-y: auto;
    width: 100%;

    &::-webkit-scrollbar {
      visibility: hidden;
    }

    &::-webkit-scrollbar-thumb {
      visibility: hidden;
    }
  }

  .v-mention {
    color: var(--color-link);
    cursor: inherit;
    text-decoration: none;
  }
}
</style>
